/**
 * Baby Development Phases Overview Page
 * Shows all 5 phases with navigation to edit each
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Baby, Calendar, FileText, Activity, Stethoscope } from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import type { BabyDevelopmentPhase } from './types';

const supabase = getSupabaseAdminClient();

export function BabyDevelopmentPhasesPage() {
  const navigate = useNavigate();
  const { contentLocale } = useAdminLocale();

  const { data: phases, isLoading } = useQuery({
    queryKey: ['baby-development-phases', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('baby_development_phases')
        .select('*')
        .eq('locale', contentLocale)
        .order('phase_number');

      if (error) throw error;
      return data as BabyDevelopmentPhase[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
            <Baby className="w-7 h-7 text-teal-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Baby-Entwicklungsphasen</h1>
            <p className="text-slate-500">Verwalte die 5 Entwicklungsphasen (0-12 Monate) mit Meilensteinen, Content und medizinischen Infos.</p>
          </div>
          <LocaleSelector />
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Baby className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Hinweis zu Zeiträumen</h3>
            <p className="text-sm text-blue-700 mt-1">
              Die Phasen-Zeiträume (z.B. "Monat 4-6") sind fest definiert und basieren auf medizinischen Standards (WHO, STIKO, BZgA).
              Subtitle, Beschreibung und alle Inhalte sind editierbar.
            </p>
          </div>
        </div>
      </div>

      {/* Phases List */}
      <div className="bg-white rounded-lg shadow-sm divide-y">
        {phases?.map((phase) => (
          <div key={phase.id} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-500">
                    Phase {phase.phase_number}
                  </span>
                  <span className="text-slate-300">•</span>
                  <span className="text-sm text-slate-500">
                    {phase.display_range}
                  </span>
                  <span className="text-slate-300">•</span>
                  <span className="text-xs text-slate-400">
                    Woche {phase.min_age_weeks}-{phase.max_age_weeks}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-slate-900">
                  "{phase.subtitle}"
                </h2>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                  {phase.description}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <button
                onClick={() => navigate(`/features/baby-development/phases/${phase.id}/edit`)}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Texte</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>

              <button
                onClick={() => navigate(`/features/baby-development/phases/${phase.id}/milestones`)}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Meilensteine</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>

              <button
                onClick={() => navigate(`/features/baby-development/phases/${phase.id}/content`)}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Baby className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Entwicklung</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>

              <button
                onClick={() => navigate(`/features/baby-development/phases/${phase.id}/activities`)}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Aktivitäten</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>

              <button
                onClick={() => navigate(`/features/baby-development/phases/${phase.id}/medical`)}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">Medizin</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
